import { useState, useRef, useEffect, useCallback } from 'preact/hooks'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import UnderlineExt from '@tiptap/extension-underline'
import LinkExt from '@tiptap/extension-link'
import { Markdown as MarkdownExt } from 'tiptap-markdown'
import { Pencil, Code } from 'lucide-preact'
import { BRAND_CLASSES } from '@/constants/theme'
import { getLabelClasses, type LabelSize } from './formLabelUtils'
import { RichTextToolbar } from './RichTextToolbar'
import { pastedHtmlToMarkdown, pasteIntoMarkdown } from '@/utils/pastedHtmlToMarkdown'
import './richtext.css'

// tiptap-markdown adds a `markdown` key to editor.storage at runtime
// but doesn't ship type augmentations, so we use a typed helper.
// Returns null when the extension's storage isn't ready yet — happens
// transiently right after editor creation when multiple RichTextBox
// instances mount together (caller must treat null as "skip the compare,
// just push the value in").
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getMarkdown(editor: any): string | null {
  const md = editor?.storage?.markdown
  if (!md || typeof md.getMarkdown !== 'function') return null
  return md.getMarkdown() as string
}

export interface RichTextBoxProps {
  /** Field label */
  label?: string
  /** Whether field is required */
  required?: boolean
  /** Field name (for htmlFor on label) */
  name: string
  /** Error message to display */
  error?: string
  /** Helper text to display below editor */
  helperText?: string
  /** Current markdown value */
  value?: string
  /** Called when content changes (markdown string) */
  onChange?: (markdown: string) => void
  /** Called on blur */
  onBlur?: () => void
  /** Placeholder text when editor is empty */
  placeholder?: string
  /** Minimum height for the editor area */
  minHeight?: string
  /** Custom class name for container */
  containerClassName?: string
  /** Label size variant */
  labelSize?: LabelSize
  /** Custom class name for label */
  labelClassName?: string
  /** Whether to show the markdown source toggle */
  showSourceToggle?: boolean
  /** Whether the editor is disabled/read-only */
  disabled?: boolean
}

/**
 * Rich text editor component with WYSIWYG toolbar and optional markdown source toggle.
 * Stores and emits markdown strings. Works with react-hook-form via Controller.
 *
 * @example
 * // With react-hook-form Controller
 * <Controller
 *   name="content"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <RichTextBox
 *       label="Content"
 *       name="content"
 *       value={field.value}
 *       onChange={field.onChange}
 *       onBlur={field.onBlur}
 *       error={fieldState.error?.message}
 *     />
 *   )}
 * />
 */
export function RichTextBox({
  label,
  required = false,
  name,
  error,
  helperText,
  value = '',
  onChange,
  onBlur,
  placeholder,
  minHeight = '160px',
  containerClassName = '',
  labelSize = 'sm',
  labelClassName = '',
  showSourceToggle = true,
  disabled = false,
}: RichTextBoxProps) {
  const [mode, setMode] = useState<'visual' | 'markdown'>('visual')
  const [markdownSource, setMarkdownSource] = useState(value)
  const skipNextUpdateRef = useRef(true)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      UnderlineExt,
      LinkExt.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary-600 dark:text-primary-400 underline',
        },
      }),
      MarkdownExt.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'prose prose-sm dark:prose-invert max-w-none px-3 py-2 focus:outline-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-headings:my-2',
        style: `min-height: ${minHeight}`,
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
      // Plain text carrying HTML tags would otherwise be stored with the tags as characters (ORB-939).
      // Runs before tiptap-markdown parses the text, so the converted Markdown renders as formatting.
      transformPastedText: (text: string) => pastedHtmlToMarkdown(text) ?? text,
    },
    onUpdate: ({ editor: ed }) => {
      if (skipNextUpdateRef.current) {
        skipNextUpdateRef.current = false
        return
      }
      const md = getMarkdown(ed)
      if (md !== null) onChange?.(md)
    },
    onBlur: () => {
      onBlur?.()
    },
  })

  // Sync external value changes into the editor (e.g. form reset).
  //
  // When several RichTextBoxes mount in parallel under preact-compat, `useEditor`
  // can hand us an Editor whose `commands` proxy / `tiptap-markdown` storage
  // isn't fully wired yet. Calling commands directly crashes in that window.
  // We try once eagerly, then also listen for the editor's `create` event so we
  // resync as soon as the editor finishes initialising.
  useEffect(() => {
    if (!editor) return

    const sync = () => {
      if (editor.isDestroyed || !editor.commands) return
      const currentMd = getMarkdown(editor)
      if (currentMd !== null && value === currentMd) return
      skipNextUpdateRef.current = true
      try {
        editor.commands.setContent(value || '')
      } catch (e) {
        console.warn('RichTextBox: editor.commands.setContent failed:', e)
      }
    }

    sync()
    editor.on('create', sync)
    return () => { editor.off('create', sync) }
  }, [value, editor])

  // Sync editable state
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    editor.setEditable(!disabled)
  }, [disabled, editor])

  const switchToVisual = useCallback(() => {
    if (editor && !editor.isDestroyed && editor.commands) {
      skipNextUpdateRef.current = true
      editor.commands.setContent(markdownSource)
    }
    setMode('visual')
  }, [editor, markdownSource])

  const switchToMarkdown = useCallback(() => {
    if (editor) {
      const md = getMarkdown(editor)
      if (md !== null) setMarkdownSource(md)
    }
    setMode('markdown')
  }, [editor])

  // The Markdown view is a plain textarea, so convert a tagged paste here too and insert it at the cursor.
  const handleMarkdownPaste = useCallback((e: ClipboardEvent) => {
    const textarea = e.currentTarget as HTMLTextAreaElement
    const paste = pasteIntoMarkdown(textarea.value, textarea.selectionStart, textarea.selectionEnd, e.clipboardData?.getData('text/plain') ?? '')
    if (paste === null) return
    e.preventDefault()
    setMarkdownSource(paste.value)
    onChange?.(paste.value)
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd = paste.cursor
    })
  }, [onChange])

  const handleMarkdownChange = useCallback((e: Event) => {
    const val = (e.target as HTMLTextAreaElement).value
    setMarkdownSource(val)
    onChange?.(val)
  }, [onChange])

  const hasError = !!error
  const borderClass = hasError
    ? BRAND_CLASSES.INPUT.ERROR_BORDER
    : BRAND_CLASSES.INPUT.NORMAL_BORDER

  return (
    <div className={containerClassName}>
      {/* Label row with mode toggle */}
      <div className="flex items-center justify-between mb-2">
        {label ? (
          <label htmlFor={name} className={getLabelClasses(labelSize, `${labelClassName} !mb-0`)}>
            {label}
            {required && <span className={`${BRAND_CLASSES.INPUT.REQUIRED} ml-1`}>*</span>}
          </label>
        ) : <div />}

        {showSourceToggle && (
          <div className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 p-0.5">
            <button
              type="button"
              onClick={mode === 'markdown' ? switchToVisual : undefined}
              className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                mode === 'visual'
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white font-medium'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <Pencil className="w-3 h-3" />
              Visual
            </button>
            <button
              type="button"
              onClick={mode === 'visual' ? switchToMarkdown : undefined}
              className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                mode === 'markdown'
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white font-medium'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <Code className="w-3 h-3" />
              Markdown
            </button>
          </div>
        )}
      </div>

      {/* Editor area */}
      {mode === 'visual' ? (
        <div
          className={`rounded-lg border ${borderClass} ${BRAND_CLASSES.INPUT.NORMAL_BG} overflow-hidden ${
            hasError ? '' : 'focus-within:ring-2 focus-within:ring-primary-500 dark:focus-within:ring-primary-400 focus-within:border-transparent'
          }`}
        >
          <RichTextToolbar editor={editor} />
          <EditorContent editor={editor} />
        </div>
      ) : (
        <textarea
          id={name}
          name={name}
          value={markdownSource}
          onInput={handleMarkdownChange}
          onPaste={handleMarkdownPaste}
          onBlur={() => onBlur?.()}
          rows={8}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent font-mono text-sm ${
            hasError
              ? BRAND_CLASSES.INPUT.ERROR
              : `${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`
          } ${BRAND_CLASSES.INPUT.PLACEHOLDER} resize-y`}
          style={{ minHeight }}
        />
      )}

      {/* Error / helper text */}
      {error && (
        <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>{error}</p>
      )}

      {helperText && !error && (
        <p className={`mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`}>{helperText}</p>
      )}
    </div>
  )
}
